import React from 'react';
import { motion } from 'framer-motion';
import {
  Calendar, Clock, MapPin, Users, MessageSquare, Heart,
  Bookmark, Mountain, Dumbbell, Car, Landmark, Wine,
  UtensilsCrossed, Laptop, Languages, Sparkles
} from 'lucide-react';
import { Activity, ActivityCategory, ACTIVITY_CATEGORIES } from '../../types/activities';
import { CATEGORY_IMAGES, CATEGORY_ACCENT, CATEGORY_GRADIENTS } from '../../constants/activity-categories';

interface ActivityCardProps {
  activity: Activity;
  onClick: () => void;
  isOwn?: boolean;
  isLiked?: boolean;
  isFavorited?: boolean;
  onLike?: (e: React.MouseEvent) => void;
  onFavorite?: (e: React.MouseEvent) => void;
}

const ICONS: Record<ActivityCategory, React.ReactNode> = {
  'hiking': <Mountain className="w-3.5 h-3.5" />,
  'sport': <Dumbbell className="w-3.5 h-3.5" />,
  'roadtrip': <Car className="w-3.5 h-3.5" />,
  'city-tour': <MapPin className="w-3.5 h-3.5" />,
  'cultural': <Landmark className="w-3.5 h-3.5" />,
  'nightlife': <Wine className="w-3.5 h-3.5" />,
  'food': <UtensilsCrossed className="w-3.5 h-3.5" />,
  'coworking': <Laptop className="w-3.5 h-3.5" />,
  'language-exchange': <Languages className="w-3.5 h-3.5" />,
  'other': <Sparkles className="w-3.5 h-3.5" />,
};

function formatActivityDate(timestamp: { toDate?: () => Date } | Date): string {
  const date = 'toDate' in timestamp && timestamp.toDate ? timestamp.toDate() : timestamp as Date;
  const now = new Date();
  const diff = date.getTime() - now.getTime();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return "Aujourd'hui";
  if (days === 1) return 'Demain';
  if (days > 1 && days <= 7) {
    return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  }
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function getDaysUntil(timestamp: { toDate?: () => Date } | Date): number {
  const date = 'toDate' in timestamp && timestamp.toDate ? timestamp.toDate() : timestamp as Date;
  return Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

export function ActivityCard({ activity, onClick, isOwn, isLiked, isFavorited, onLike, onFavorite }: ActivityCardProps) {
  const spotsLeft = activity.maxParticipants - activity.participants.length;
  const isFull = spotsLeft <= 0;
  const catInfo = ACTIVITY_CATEGORIES.find(c => c.id === activity.category);
  const accent = CATEGORY_ACCENT[activity.category];
  const daysUntil = getDaysUntil(activity.date);
  const isUrgent = daysUntil <= 2 && daysUntil >= 0;
  const likesCount = activity.likes?.length || 0;
  const commentsCount = activity.comments?.length || 0;
  const participantAvatars = activity.participants.slice(0, 4);
  const remainingParticipants = activity.participants.length - 4;

  return (
    <motion.button
      onClick={onClick}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      className="w-full text-left rounded-2xl overflow-hidden bg-white dark:bg-gray-800 shadow-sm hover:shadow-xl border border-gray-100 dark:border-gray-700/50 transition-shadow duration-300 group"
    >
      <div className="relative h-44 sm:h-48 overflow-hidden">
        <img
          src={CATEGORY_IMAGES[activity.category]}
          alt=""
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        <div className={`absolute inset-0 bg-gradient-to-t ${CATEGORY_GRADIENTS[activity.category]} opacity-40`} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        <div className="absolute top-3 left-3 right-3 flex items-start justify-between">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white/90 dark:bg-gray-900/80 text-gray-800 dark:text-gray-200 backdrop-blur-sm shadow-sm">
              {ICONS[activity.category]}
              {catInfo?.label}
            </span>
            {isOwn && (
              <span className="px-2 py-1 bg-teal-500/90 text-white text-[11px] font-semibold rounded-full backdrop-blur-sm">
                Mon annonce
              </span>
            )}
            {isUrgent && !isOwn && (
              <span className="px-2 py-1 bg-red-500/90 text-white text-[11px] font-bold rounded-full backdrop-blur-sm animate-pulse">
                {daysUntil === 0 ? "Aujourd'hui !" : 'Demain !'}
              </span>
            )}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onFavorite?.(e); }}
            className={`p-2 rounded-full backdrop-blur-sm transition-all ${
              isFavorited
                ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                : 'bg-white/80 dark:bg-gray-900/60 text-gray-600 dark:text-gray-300 hover:bg-white'
            }`}
          >
            <Bookmark className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
          </button>
        </div>

        <div className="absolute bottom-3 left-3 right-3">
          <div className="flex items-end justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full ${accent.bg} flex items-center justify-center text-white text-xs font-bold ring-2 ring-white/30`}>
                {activity.username.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-white text-xs font-medium leading-tight drop-shadow-sm">{activity.username}</p>
                <p className="text-white/70 text-[10px] flex items-center gap-1">
                  <MapPin className="w-2.5 h-2.5" />
                  {activity.city}
                </p>
              </div>
            </div>
            {activity.participants.length > 0 && (
              <div className="flex -space-x-2">
                {participantAvatars.map((p) => (
                  <div
                    key={p.userId}
                    className="w-7 h-7 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white/40 flex items-center justify-center text-white text-[10px] font-bold"
                    title={p.username}
                  >
                    {p.username.charAt(0).toUpperCase()}
                  </div>
                ))}
                {remainingParticipants > 0 && (
                  <div className="w-7 h-7 rounded-full bg-black/30 backdrop-blur-sm border-2 border-white/40 flex items-center justify-center text-white text-[10px] font-bold">
                    +{remainingParticipants}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-[15px] font-bold text-gray-900 dark:text-white mb-2 line-clamp-2 leading-snug group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
          {activity.title}
        </h3>

        <p className="text-[13px] text-gray-500 dark:text-gray-400 line-clamp-2 mb-3 leading-relaxed">
          {activity.description}
        </p>

        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center gap-1.5 text-[13px] text-gray-600 dark:text-gray-400">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            <span className="font-medium">{formatActivityDate(activity.date)}</span>
          </div>
          <span className="text-gray-200 dark:text-gray-700">|</span>
          <div className="flex items-center gap-1.5 text-[13px] text-gray-600 dark:text-gray-400">
            <Clock className="w-3.5 h-3.5 text-gray-400" />
            <span>{activity.time}</span>
          </div>
        </div>

        {(activity.difficulty || activity.budget) && (
          <div className="flex items-center gap-1.5 mb-3">
            {activity.difficulty && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 font-medium">
                {activity.difficulty === 'easy' ? '🟢 Facile' : activity.difficulty === 'moderate' ? '🟡 Modéré' : '🔴 Sportif'}
              </span>
            )}
            {activity.budget && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 font-medium">
                {activity.budget === 'free' ? 'Gratuit' : activity.budget === 'cheap' ? '< 20 €' : activity.budget === 'moderate' ? '20-50 €' : '> 50 €'}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700/50">
          <div className="flex items-center gap-1.5">
            <Users className={`w-4 h-4 ${isFull ? 'text-red-500' : spotsLeft <= 3 ? 'text-amber-500' : 'text-teal-500'}`} />
            <span className={`text-[13px] font-semibold ${isFull ? 'text-red-600 dark:text-red-400' : spotsLeft <= 3 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {activity.participants.length}/{activity.maxParticipants}
            </span>
            {isFull ? (
              <span className="text-[11px] text-red-500 font-medium px-1.5 py-0.5 bg-red-50 dark:bg-red-900/20 rounded">Complet</span>
            ) : spotsLeft <= 3 ? (
              <span className="text-[11px] text-amber-600 dark:text-amber-400">{spotsLeft} place{spotsLeft > 1 ? 's' : ''}</span>
            ) : null}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={(e) => { e.stopPropagation(); onLike?.(e); }}
              className={`flex items-center gap-1 text-[13px] transition-colors ${
                isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
              }`}
            >
              <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
              {likesCount > 0 && <span className="font-medium">{likesCount}</span>}
            </button>
            {commentsCount > 0 && (
              <span className="flex items-center gap-1 text-[13px] text-gray-400">
                <MessageSquare className="w-4 h-4" />
                <span>{commentsCount}</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.button>
  );
}
